import React from "react"
import styled from "styled-components"
import {connect} from "react-redux"
import {
    scaleBand,
    scaleSqrt,
} from "d3-scale"
import Axis from "../../../../src/components/charts/common/axis/axis"
import Grid from "../../../../src/components/charts/common/axis/grid"
import Bar from "../../../../src/components/charts/bar-chart/bar-2"

const getInitialData = (data, xKey, yKey, y1Key) => {
    if (data.length < 1) {
        return []
    }

    return data.map((bar) => {
        let item = {}
        item[xKey] = bar[xKey]
        item[yKey] = 0
        item[y1Key] = 0

        return item
    })
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = {error: null, errorInfo: null}
    }

    componentDidCatch(error, errorInfo) {
        // Catch errors in any components below and re-render with error message
        this.setState({
            error: error,
            errorInfo: errorInfo,
        })
        // You can also log error messages to an error reporting service here
    }

    render() {
        if (this.state.errorInfo) {
            // Error path
            return (
                <div>
                    <h2>Something went wrong.</h2>
                    <details style={{whiteSpace: "pre-wrap"}}>
                        {this.state.error && this.state.error.toString()}
                        <br/>
                        {this.state.errorInfo.componentStack}
                    </details>
                </div>
            )
        }
        // Normally, just render children
        return this.props.children
    }
}

class LayeredBarChart extends React.Component {
    constructor() {
        super()
        this.state = {
            parentWidth: 0,
            tooltipData: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                data: null,
            },
        }
        this.ref = React.createRef()
    }

    componentDidMount() {
        this.fitParentContainer()
        window.addEventListener("resize", this.fitParentContainer)
    }

    fitParentContainer = () => {
        const {parentWidth} = this.state

        if (!this.ref.current) return;

        const currentParentWidth =
            this.ref.current && this.ref.current.getBoundingClientRect().width

        const shouldResize = parentWidth !== currentParentWidth
        if (shouldResize) {
            this.setState({
                parentWidth: currentParentWidth,
            })
        }
    }

    handleTooltip = tooltipData => {
        this.setState({
            tooltipData: {...this.state.tooltipData, ...tooltipData},
        })

    }

    render() {
        const {yKey, y1Key, projectType, selectedLanguage, tooltip: ToolTip} = this.props
        let {xKey, chartData} = this.props


        const {tooltipData, parentWidth} = this.state
        const initialBarData = getInitialData(chartData, xKey, yKey, y1Key)

        if (parentWidth === 0) {
            return <div ref={this.ref}/>
        }

        // set the dimensions and margins of the graph
        let margin = {
            top: 12,
            right: 24,
            bottom: 60,
            left: 32,
        }

        if(document.body.clientWidth < 600) {
            margin.right = 8
        }

        function Yaxis(ykey) {
            return <span className={"bar-y-label"}>{ykey}</span>
        }

        // const width = parentWidth - 100
        // const height = 500;

        const width = parentWidth - margin.left - margin.right
        const height = 260

        // creating scales
        const xScale = scaleBand()
            .domain(chartData.map(d => d[xKey]))
            .range([0, width])
            .padding(0.5)

        const yScale = scaleSqrt()
            .domain([0, Math.max(...chartData.map(d => d[yKey]))])
            .range([height, 0])

        return (
            <BarWrapper
                innerRef={this.ref}
                className="bar-chart"
                margin={margin}
                xScale={xScale}
                tooltipData={tooltipData}
            >
                <ToolTip {...tooltipData.data}/>
                {yKey === "wards" && <Yaxis/>}

                <svg width={width + margin.left + margin.right}
                     height={height + margin.top + margin.bottom}
                >
                    <g transform={`translate(40, 16)`}>
                        <g className="axis-group">
                            <Axis valueType={"number"} scale={xScale} orient="bottom"
                                  transform={`translate(0, ${height})`}/>
                            <Axis highAmount={chartData} scale={yScale} orient="left"
                                  transform={`translate(-2px,0)`}
                                  valueType={"number"}
                                  selectedLanguage={selectedLanguage}/>
                        </g>
                        <Grid {...{xScale, yScale, width}} />
                        <Bar roundedFull={true}
                             {...{
                                 xKey,
                                 yKey,
                                 y1Key,
                                 xScale,
                                 yScale,
                                 width,
                                 height,
                                 chartData,
                                 initialBarData,
                                 projectType,
                                 onMouseEnter: this.handleTooltip,
                             }}
                        />
                    </g>
                </svg>
                <div className={"axis-label"}>
                    <span className={'axis-label-x'}>{this.props.xLabel}</span>
                    <span style={{left: selectedLanguage === 'np' ? '-32px' : '-40px'}}
                          className={'axis-label-y'}>{this.props.yLabel}</span>
                </div>
            </BarWrapper>
        )
    }
}

const BarWrapper = styled.div`
    position: relative;
    .tooltip
        {
            position: absolute
            visibility: ${props => props.tooltipData.left === 0 ? "hidden" : "visible"}
                left: ${props => `${props.tooltipData.left + props.margin.left}px`};
    bottom: ${props => `${props.tooltipData.bottom + props.margin.bottom + 15}px`};
    transform: ${props => `translateX(calc(-50% + ${props.xScale.bandwidth() * 0.5}px))`};
  }
  /* ******** grid ************ */
  .grid-group line {
    stroke: #5d636d;
    stroke-opacity: 0.2;
    shape-rendering: crispEdges;
  }
  .grid-group path {
    stroke-width: 0;
  }
  /* *********** axis  *********** */
  .axis-group path,
  .axis-group line {
    stroke-width: 0;
  }
  .left-axis text {
    fill: var(--color-theme-1);
    opacity: 0.2;
    font-size: 12px;
  }
  .bottom-axis text {
    fill: var(--color-theme-1);
    opacity: 0.5;
    font-size: 12px;
    text-transform: capitalize;
  }
`

export default connect(state => ({
    selectedLanguage: state.localizationReducers.selectedLanguage,
}))(LayeredBarChart)



// WEBPACK FOOTER //
// ./src/components/layouts/WardChart/ward-layered-bar-chart.js